import { HTMLAttributes, ReactNode } from "react";

/**
 * Badge — Archival Studio primitive.
 *
 * Pills are reserved for proof-gate state. Each variant is a state from
 * the editorial workflow.
 *
 *   verified    — green, "Verified — 14 of 14 claims sourced"
 *   in-review   — proof slate, "In review"
 *   take        — walnut, "Take 04" (which draft we're on)
 *   contested   — flag-red, "Contested" (used only when a claim fails)
 *   draft       — paper-2 / ink-3, "Draft" (pre-review)
 *
 * Source: project/preview/components-badges.html
 */

type Tone = "verified" | "in-review" | "take" | "contested" | "draft";

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone: Tone;
  children: ReactNode;
}

const TONE: Record<Tone, { bg: string; fg: string; dot: string }> = {
  "verified":   { bg: "bg-verify-tint",  fg: "text-verify", dot: "bg-verify" },
  "in-review":  { bg: "bg-proof-tint",   fg: "text-proof", dot: "bg-proof" },
  "take":       { bg: "bg-walnut-tint",  fg: "text-walnut-deep", dot: "bg-walnut" },
  "contested":  { bg: "bg-flag-tint",    fg: "text-flag", dot: "bg-flag" },
  "draft":      { bg: "bg-paper-2",      fg: "text-ink-3",     dot: "bg-ink-4" },
};

export function Badge({ tone, children, className = "", ...rest }: BadgeProps) {
  const t = TONE[tone];
  return (
    <span
      className={
        "inline-flex items-center gap-[6px] " +
        "rounded-as-pill px-[10px] py-[4px] " +
        "font-mono text-[11px] tracking-as-eyebrow uppercase " +
        `${t.bg} ${t.fg} ${className}`
      }
      {...rest}
    >
      <span className={`w-[6px] h-[6px] rounded-full ${t.dot}`} />
      {children}
    </span>
  );
}
